<template>
  <div class="w-full">
    <!-- Single-select mode -->
    <a-select
      v-if="single"
      :value="singleSelectedKey || undefined"
      class="w-full record_type_selector"
      :placeholder="placeholder"
      :loading="isSearching"
      show-search
      :filter-option="false"
      allow-clear
      option-label-prop="label"
      :get-popup-container="getPopupContainer"
      :dropdown-class-name="'record_type_selector-dropdown'"
      @search="handleSearch"
      @change="handleSingleChange"
      @dropdown-visible-change="handleDropdownVisibleChange"
      @popupScroll="handlePopupScroll"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="dropdown"
          class="size-4"
        />
      </template>

      <template #notFoundContent>
        <div v-if="isSearching" class="flex items-center justify-center py-4">
          <a-spin
            size="small"
            class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
          />
        </div>
        <EmptyScreen
          v-else-if="!relatedObjects || relatedObjects.length === 0"
          source="record-type-no-objects"
          text="No object types configured"
          css="size-10"
          padding="py-4"
        />
        <EmptyScreen
          v-else
          source="record-type-no-results"
          :text="
            currentSearchQuery
              ? 'No records found'
              : 'Type to search or select from below'
          "
          css="size-10"
          padding="py-4"
        />
      </template>

      <a-select-opt-group
        v-for="group in searchResults"
        :key="group.object_id"
        :label="group.object_name"
      >
        <a-select-option
          v-for="record in group.records"
          :key="`${group.object_id}:${record.id}`"
          :value="`${group.object_id}:${record.id}`"
          :label="record.name"
        >
          <div class="flex items-center gap-2">
            <ProfileIcon :data="{ name: record.name }" :size="20" :font="10" />
            <a-tooltip :title="record.name" placement="top" color="var(--tooltip-bg)">
              <span class="text-sm text_color truncate">{{ record.name }}</span>
            </a-tooltip>
            <span class="text-xs secondary_color ml-auto">{{
              group.object_name
            }}</span>
          </div>
        </a-select-option>
      </a-select-opt-group>

      <a-select-option v-if="isLoadingMore" key="__loading_more__" disabled>
        <div class="flex items-center justify-center py-2">
          <a-spin
            size="small"
            class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
          />
        </div>
      </a-select-option>

      <template v-if="canCreate" #dropdownRender="{ menuNode }">
        <component :is="menuNode" />
        <div class="create_new_section">
          <div class="create_new_trigger" @click="handleOpenCreateModal">
            <ThemeImage :icon="imageNames.PLUS_GREY" class="size-4" />
            <span class="text-sm">{{ createLabel || "Create New" }}</span>
          </div>
        </div>
      </template>
    </a-select>

    <!-- Multi-select mode -->
    <a-select
      v-else
      v-model:value="selectedRecords"
      v-model:open="dropdownOpen"
      mode="multiple"
      class="w-full record_type_selector"
      :placeholder="placeholder"
      :loading="isSearching"
      show-search
      :show-arrow="false"
      :filter-option="false"
      :max-tag-count="'responsive'"
      :get-popup-container="getPopupContainer"
      :dropdown-class-name="'record_type_selector-dropdown'"
      @search="handleSearch"
      @change="handleChange"
      @dropdown-visible-change="handleDropdownVisibleChange"
      @popupScroll="handlePopupScroll"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="dropdown"
          class="size-4"
        />
      </template>

      <template #tagRender="{ value, closable, onClose }">
        <a-tag
          :closable="closable"
          class="action_small record_tag_pill"
          @close="onClose"
          @mousedown="(e) => e.preventDefault()"
        >
          <ProfileIcon
            :data="{ name: getRecordName(value) }"
            :size="18"
            :font="9"
          />
          <a-tooltip :title="getRecordName(value)" placement="top" color="var(--tooltip-bg)">
            <span class="record_tag_name">{{ getRecordName(value) }}</span>
          </a-tooltip>
        </a-tag>
      </template>

      <template #notFoundContent>
        <div v-if="isSearching" class="flex items-center justify-center py-4">
          <a-spin
            size="small"
            class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
          />
        </div>
        <EmptyScreen
          v-else-if="!relatedObjects || relatedObjects.length === 0"
          source="record-type-no-objects"
          text="No object types configured"
          css="size-10"
          padding="py-4"
        />
        <EmptyScreen
          v-else
          source="record-type-no-results"
          :text="
            currentSearchQuery
              ? 'No records found'
              : 'Type to search or select from below'
          "
          css="size-10"
          padding="py-4"
        />
      </template>

      <a-select-opt-group
        v-for="group in searchResults"
        :key="group.object_id"
        :label="group.object_name"
      >
        <a-select-option
          v-for="record in group.records"
          :key="`${group.object_id}:${record.id}`"
          :value="`${group.object_id}:${record.id}`"
          :label="record.name"
        >
          <div class="flex items-center gap-2">
            <ProfileIcon :data="{ name: record.name }" :size="20" :font="10" />
            <a-tooltip :title="record.name" placement="top" color="var(--tooltip-bg)">
              <span class="text-sm text_color truncate">{{ record.name }}</span>
            </a-tooltip>
            <span class="text-xs secondary_color ml-auto">{{
              group.object_name
            }}</span>
          </div>
        </a-select-option>
      </a-select-opt-group>

      <a-select-option v-if="isLoadingMore" key="__loading_more__" disabled>
        <div class="flex items-center justify-center py-2">
          <a-spin
            size="small"
            class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
          />
        </div>
      </a-select-option>

      <template v-if="canCreate" #dropdownRender="{ menuNode }">
        <component :is="menuNode" />
        <div class="create_new_section">
          <div class="create_new_trigger" @click="handleOpenCreateModal">
            <ThemeImage :icon="imageNames.PLUS_GREY" class="size-4" />
            <span class="text-sm">{{ createLabel || "Create New" }}</span>
          </div>
        </div>
      </template>
    </a-select>
    <Teleport to="body">
      <CustomModal
        v-if="canCreate"
        v-model:visible="showCreateModal"
        :title="`Create ${createObjectName}`"
        :width="'500px'"
        :section_classes="{
          container: 'record_type_create_modal',
          body: 'record_type_create_modal_body',
        }"
      >
        <AddNewDynamicEntityForm
          v-if="showCreateModal && createObjectSchema"
          ref="createFormRef"
          :slug="createObjectSlug"
          :properties="createObjectSchema.attributes || []"
          :visible="showCreateModal"
          :entity-display-name="createObjectName"
          :hide-parent-field="true"
          :initial-data="createInitialData"
          :field-tooltips="createFieldTooltips"
          :service="createObjectService"
          @form-ready="handleCreateFormReady"
        />
        <template #footer>
          <div class="flex justify-end gap-2">
            <a-button class="secondary_medium" @click="showCreateModal = false"
              >Cancel</a-button
            >
            <a-button
              class="primary_medium"
              :loading="isCreating"
              @click="handleCreateFromModal"
            >
              {{ isCreating ? "Creating..." : `Create ${createObjectName}` }}
            </a-button>
          </div>
        </template>
      </CustomModal>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import debounce from "lodash/debounce.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import AddNewDynamicEntityForm from "@/src/views/dynamic_crm/AddNewDynamicEntityForm.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import CRMService from "@/src/api/CRMService.js";
import { resolveHasMore } from "@/src/composables/useSelectInfiniteScroll.js";
import { transformFormDataForAPI } from "@/src/utils/dynamicFormUtils.js";
import { getServiceForSlug } from "@/src/constants/crmConstants.js";
import { useStore } from "vuex";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  relatedObjects: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Search and select records...",
  },
  attribute: {
    type: Object,
    default: () => ({}),
  },
  // When true, renders as a single-select dropdown (no tags, one value).
  single: {
    type: Boolean,
    default: false,
  },
  createOptions: {
    type: Object,
    default: null,
  },
});

import { message } from "ant-design-vue";

const emit = defineEmits(["update:modelValue"]);

const store = useStore();

const isSearching = ref(false);
const searchResults = ref([]);
const selectedRecords = ref([]);
const dropdownOpen = ref(false);
const isLoadingMore = ref(false);

const paginationState = ref({ page: 1, hasMore: true, query: "" });
const PAGE_SIZE = 20;

const currentSearchQuery = computed(() => paginationState.value.query);

// --- Single-select helpers ---

const singleSelectedKey = computed(() => {
  const record = (props.modelValue || [])[0];
  if (!record?.object_type_id || !record?.record_id) return undefined;
  return `${record.object_type_id}:${record.record_id}`;
});

const handleSingleChange = (compositeKey) => {
  if (!compositeKey) {
    emit("update:modelValue", []);
    return;
  }
  const [objectId, entityId] = compositeKey.split(":");
  for (const group of searchResults.value) {
    if (group.object_id === objectId) {
      const record = group.records.find((r) => r.id === entityId);
      if (record) {
        emit("update:modelValue", [
          {
            object_type_id: objectId,
            record_id: entityId,
            record_display_name: record.name,
          },
        ]);
        return;
      }
    }
  }
  emit("update:modelValue", [
    { object_type_id: objectId, record_id: entityId },
  ]);
};

// --- Multi-select helpers ---

const getRecordsArray = () => {
  if (Array.isArray(props.modelValue)) return props.modelValue;
  if (Array.isArray(props.modelValue?.records)) return props.modelValue.records;
  return [];
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.single) return;
    let records = [];
    if (Array.isArray(newValue)) {
      records = newValue;
    } else if (Array.isArray(newValue?.records)) {
      records = newValue.records;
    }
    selectedRecords.value = records
      .filter(
        (r) => r && typeof r === "object" && r.object_type_id && r.record_id,
      )
      .map((r) => `${r.object_type_id}:${r.record_id}`);
  },
  { immediate: true },
);

const getRecordName = (compositeKey) => {
  if (!compositeKey) return "";
  const [objectId, entityId] = compositeKey.split(":");
  const existing = getRecordsArray().find(
    (r) => r.object_type_id === objectId && r.record_id === entityId,
  );
  if (existing?.record_display_name) return existing.record_display_name;
  for (const group of searchResults.value) {
    if (group.object_id === objectId) {
      const record = group.records.find((r) => r.id === entityId);
      if (record?.name) return record.name;
    }
  }
  return entityId || "";
};

const handleChange = (values) => {
  const existingRecords = getRecordsArray();
  const records = values
    .map((compositeKey) => {
      if (typeof compositeKey === "object" && compositeKey !== null)
        return compositeKey;
      if (typeof compositeKey !== "string") return null;
      const [objectId, entityId] = compositeKey.split(":");
      const existing = existingRecords.find(
        (r) => r.object_type_id === objectId && r.record_id === entityId,
      );
      if (existing) return existing;
      for (const group of searchResults.value) {
        if (group.object_id === objectId) {
          const record = group.records.find((r) => r.id === entityId);
          if (record) {
            return {
              object_type_id: objectId,
              record_id: entityId,
              record_display_name: record.name,
            };
          }
        }
      }
      return { object_type_id: objectId, record_id: entityId };
    })
    .filter(Boolean);
  emit("update:modelValue", records);
  dropdownOpen.value = true;
  // Reset search so the dropdown shows the full list for the next selection
  paginationState.value.query = "";
  performSearch("");
};

// --- Shared: search + pagination ---

const mapResults = (response) => {
  if (!response?.success || !response?.results) return [];
  return response.results.map((objResult) => ({
    object_id: objResult.object_id,
    object_type: objResult.object_type,
    object_name: objResult.object_name,
    records: (objResult.records || []).map((record) => ({
      id: record.id,
      name: record?.attributes?.display_name || record.id,
      object_id: objResult.object_id,
    })),
  }));
};

const fetchPage = async (page, query, { signal, seq } = {}) => {
  if (!props.relatedObjects?.length) return;
  try {
    const response = await CRMService.searchMultiObjects(
      {
        object_ids: props.relatedObjects.map((ro) => ro.object_id),
        query: query || "",
        limit: PAGE_SIZE,
        page,
      },
      { signal },
    );
    if (seq !== undefined && seq !== searchSeq) return;
    const mapped = mapResults(response);
    if (page === 1) {
      searchResults.value = mapped;
    } else {
      for (const newGroup of mapped) {
        const existing = searchResults.value.find(
          (g) => g.object_id === newGroup.object_id,
        );
        if (existing) {
          const existingIds = new Set(existing.records.map((r) => r.id));
          const unique = newGroup.records.filter((r) => !existingIds.has(r.id));
          existing.records.push(...unique);
        } else {
          searchResults.value.push(newGroup);
        }
      }
    }
    // Use resolveHasMore per group; stop if ALL groups say no more.
    const groups = response?.results || [];
    const allExhausted =
      groups.length > 0 &&
      groups.every(
        (g) =>
          !resolveHasMore(
            { has_more: g.has_more, items: g.records || [] },
            PAGE_SIZE,
          ),
      );
    paginationState.value.hasMore = !allExhausted;
    paginationState.value.page = page;
  } catch (error) {
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
    console.error("Error searching records:", error);
    if (page === 1) searchResults.value = [];
  }
};

let searchSeq = 0;
let searchAbort = null;

const performSearch = async (query = "") => {
  const seq = ++searchSeq;
  if (searchAbort) searchAbort.abort();
  searchAbort = new AbortController();

  paginationState.value = { page: 1, hasMore: true, query };
  isSearching.value = true;
  try {
    await fetchPage(1, query, { signal: searchAbort.signal, seq });
  } finally {
    if (seq === searchSeq) isSearching.value = false;
  }
};

const loadMore = async () => {
  if (
    !paginationState.value.hasMore ||
    isSearching.value ||
    isLoadingMore.value
  )
    return;
  isLoadingMore.value = true;
  const seq = searchSeq;
  try {
    await fetchPage(
      paginationState.value.page + 1,
      paginationState.value.query,
      { signal: searchAbort?.signal, seq },
    );
  } finally {
    if (seq === searchSeq) isLoadingMore.value = false;
  }
};

const handlePopupScroll = (event) => {
  const el = event.target;
  if (!el) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
    loadMore();
  }
};

const handleSearch = debounce((query) => {
  performSearch(query);
}, 300);

onBeforeUnmount(() => {
  handleSearch.cancel();
  if (searchAbort) searchAbort.abort();
});

const handleDropdownVisibleChange = (visible) => {
  dropdownOpen.value = visible;
  if (!visible) {
    paginationState.value.query = "";
  }
};

const getPopupContainer = () => {
  return typeof document !== "undefined" ? document.body : undefined;
};

onMounted(() => {
  if (props.relatedObjects?.length) {
    performSearch("");
  }
});

watch(
  () => props.relatedObjects,
  (newVal) => {
    if (newVal?.length && !searchResults.value.length) {
      performSearch("");
    }
  },
  { immediate: false },
);

// --- Create New record via modal ---

const showCreateModal = ref(false);
const isCreating = ref(false);
const createFormRef = ref(null);
const createFormMethods = ref(null);
const createObjectSchema = ref(null);

const createObjectSlug = computed(
  () => props.relatedObjects?.[0]?.object_type || "",
);
// Prefer the relation's explicit service, fall back to slug lookup.
const createObjectService = computed(
  () =>
    props.relatedObjects?.[0]?.service ||
    getServiceForSlug(createObjectSlug.value, store) ||
    null,
);
const createObjectName = computed(
  () => createObjectSchema.value?.object?.name || createLabel.value || "Record",
);

const handleOpenCreateModal = async () => {
  // Close the dropdown first
  dropdownOpen.value = false;

  // Fetch schema for the target object so the form renders all fields
  if (!createObjectSchema.value && createObjectSlug.value) {
    try {
      const res = await CRMService.fetchSchemaObject(
        createObjectSlug.value,
        createObjectService.value,
      );
      if (res?.success) {
        createObjectSchema.value = res.schema || res.data;
      }
    } catch (e) {
      console.error("Failed to fetch schema for create modal:", e);
    }
  }
  showCreateModal.value = true;
};

const handleCreateFormReady = (methods) => {
  createFormMethods.value = methods;
};

const handleCreateFromModal = async () => {
  if (isCreating.value || !createFormMethods.value) return;

  // Validate
  try {
    await createFormMethods.value.validateForm();
  } catch (error) {
    console.error("Form validation failed:", error);
    return;
  }

  const formData = createFormMethods.value.getFormData();
  const filteredProps = createFormMethods.value.getFilteredProperties?.() || [];
  const transformed = transformFormDataForAPI(formData, filteredProps, null, {
    allowExplicitFalse: true,
  });

  const targetObject = props.relatedObjects?.[0];
  if (!targetObject) return;

  isCreating.value = true;
  try {
    const res = await CRMService.createDynamicEntity(
      createObjectSlug.value,
      {
        attributes: transformed.attributes || {},
        relations: transformed.relations || {},
      },
      createObjectService.value,
    );

    const newId = res?.record?.id || res?.data?._id || res?.data?.id;
    if (!newId) {
      message.error("Failed to create record");
      return;
    }

    // Resolve display name from form data (primary attribute)
    const primaryAttr = (createObjectSchema.value?.attributes || []).find(
      (a) => a.is_primary,
    );
    const displayName = formData[primaryAttr?.slug] || formData.name || newId;

    // Add the new record to searchResults so it appears in the dropdown.
    const groupIdx = searchResults.value.findIndex(
      (g) => g.object_id === targetObject.object_id,
    );
    const newRecord = {
      id: newId,
      name: displayName,
      object_id: targetObject.object_id,
    };
    if (groupIdx >= 0) {
      searchResults.value[groupIdx].records.unshift(newRecord);
    } else {
      searchResults.value.unshift({
        object_id: targetObject.object_id,
        object_type: targetObject.object_type,
        object_name: createObjectName.value,
        records: [newRecord],
      });
    }

    // Auto-select the new record.
    if (props.single) {
      emit("update:modelValue", [
        {
          object_type_id: targetObject.object_id,
          record_id: newId,
          record_display_name: displayName,
        },
      ]);
    } else {
      const existing = getRecordsArray();
      emit("update:modelValue", [
        ...existing,
        {
          object_type_id: targetObject.object_id,
          record_id: newId,
          record_display_name: displayName,
        },
      ]);
    }

    message.success(`"${displayName}" created`);
    showCreateModal.value = false;
    createFormMethods.value = null;
  } catch (error) {
    console.error("Failed to create record:", error);
    message.error("Failed to create record");
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ selectedRecords });

const canCreate = computed(() => !!props.createOptions);
const createLabel = computed(() => props.createOptions?.label || "");
const createInitialData = computed(() => props.createOptions?.initialData || {});
const createFieldTooltips = computed(() => props.createOptions?.fieldTooltips || {});
</script>

<style scoped>
.record_type_selector :deep(.ant-select-selector) {
  min-height: 36px;
  padding: 2px 8px;
  border: 1px solid var(--white-opacity-8) !important;
  border-radius: 8px !important;
  background-color: var(--white-opacity-4) !important;
  box-shadow: none !important;
}

.record_type_selector:hover :deep(.ant-select-selector) {
  border-color: var(--white-opacity-20) !important;
}

.record_type_selector
  :deep(.ant-select-focused.ant-select .ant-select-selector),
.record_type_selector.ant-select-focused :deep(.ant-select-selector) {
  border-color: var(--white-opacity-20) !important;
  box-shadow: none !important;
}

.record_type_selector :deep(.ant-select-selection-overflow) {
  gap: 4px;
}

.record_tag_pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 2px 0;
  padding: 3px 8px 3px 4px;
  border-radius: 999px;
  background: var(--white-opacity-4);
  border: 1px solid var(--white-opacity-8);
  color: var(--foreground-rgb);
  line-height: 1.4;
}

.record_tag_name {
  font-size: 13px;
  font-weight: 500;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record_tag_pill :deep(.ant-tag-close-icon) {
  margin-left: 2px;
  color: var(--secondary-text-color);
  font-size: 12px;
}

.record_tag_pill :deep(.ant-tag-close-icon:hover) {
  color: var(--foreground-rgb);
}

.create_new_section {
  border-top: 1px solid var(--border-color);
  padding: 8px 12px;
}

.create_new_trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 0;
  color: var(--secondary-text-color);
  transition: color 0.15s;
}

.create_new_trigger:hover {
  color: var(--foreground-rgb);
}

.ant-select-dropdown.record_type_selector-dropdown {
  z-index: 10000 !important;
}
</style>

<!-- Unscoped styles for the teleported create modal: section_classes are
     applied to CustomModal's own template elements (which carry that
     component's scope id, not ours), so scoped selectors from this file
     wouldn't match. These class names are specific enough not to collide. -->
<style>
.record_type_create_modal {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.record_type_create_modal_body {
  overflow-y: auto;
  max-height: calc(85vh - 120px);
}
</style>
